import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import { NotTranslated, T, TranslationsContext, translation } from '../../../../../../Translations';
import InternalName from './InternalName';
import Title from './Title';
import { useReadyBuilder } from './useReadyBuilder';

interface Props {
  children: React.ReactNode;
  isCollapsed: boolean;
  stateKind: 'edit' | 'new';
  sectionCount: number;
  totalPrograms: number;
  hasUnlicensedPrograms: boolean;
  onLicenseWarningClick: () => void;
  onSectionsButtonClick: () => void;
}

function BuilderV2HeaderView({
  children,
  isCollapsed,
  stateKind,
  sectionCount,
  totalPrograms,
  hasUnlicensedPrograms,
  onLicenseWarningClick,
  onSectionsButtonClick,
}: Props) {
  const ts = useContext(TranslationsContext);
  const {
    resourceState,
    handleLearningCollectionInternalNameChange,
    handleLearningCollectionNameChange,
  } = useReadyBuilder();
  const { internal_name, name } = resourceState.payload;

  return (
    <div className="bg-card border-b border-border">
      <div
        className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14"
        style={{
          transitionProperty: 'padding-top, padding-bottom',
          transitionDuration: '300ms',
          transitionTimingFunction: 'ease-out',
          paddingTop: isCollapsed ? '0.625rem' : '1.25rem',
          paddingBottom: isCollapsed ? '0.625rem' : '1.25rem',
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {isCollapsed ? (
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="min-w-0 flex-1">
                  <Title
                    name={name}
                    handleLearningCollectionNameChange={handleLearningCollectionNameChange}
                    compact
                  />
                </div>
                <button
                  onClick={onSectionsButtonClick}
                  className="flex items-center focus:outline-none hover:opacity-80 transition-opacity text-muted-foreground flex-shrink-0"
                  style={{ fontSize: 'var(--text-sm)' }}
                  data-intercom-target="builder-v2-collapsed-nav-sections-button"
                >
                  <span>
                    {sectionCount}{' '}
                    {sectionCount === 1 ? <T kind="Section" /> : <T kind="Sections" />}
                    {' • '}
                    {totalPrograms}{' '}
                    {totalPrograms === 1 ? <T kind="Course" /> : <T kind="Courses" />}
                  </span>
                </button>
                {hasUnlicensedPrograms && (
                  <>
                    <button
                      onClick={onLicenseWarningClick}
                      className="p-1 hover:opacity-80 transition-opacity flex-shrink-0 text-destructive"
                      aria-label="View license details"
                      data-intercom-target="builder-v2-collapsed-nav-license-warning-button"
                      data-tooltip-id="collapsed-license-warning-tooltip"
                      data-tooltip-content="Some programs require additional licensing. Click to view details."
                    >
                      <ExclamationTriangleIcon className="h-5 w-5" />
                    </button>
                    <Tooltip id="collapsed-license-warning-tooltip" place="bottom" />
                  </>
                )}
              </div>
            ) : (
              <div>
                {/* DVLP-7604: the Edit screen shows the collection's actual name;
                    only the New screen shows the generic "Create a new collection"
                    heading. */}
                <h3 className="mb-2" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  {stateKind === 'edit' ? (
                    <NotTranslated text={name} />
                  ) : (
                    <T kind="Create a new collection" />
                  )}
                </h3>
                <InternalName
                  internalName={internal_name.getOrElseValue('')}
                  handleLearningCollectionInternalNameChange={
                    handleLearningCollectionInternalNameChange
                  }
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label={translation('Help')(ts)}
              data-intercom-target="builder-v2-help-button"
            >
              <QuestionMarkCircleIcon className="w-5 h-5 text-muted-foreground" />
            </button>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(BuilderV2HeaderView);
